import React from 'react';
import { Check, ArrowRight } from 'lucide-react';

const Services = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const packages = [
    {
      name: 'Cinematic Trailer',
      price: '$400 - $800',
      description: 'One powerful cinematic book trailer that captures your story',
      features: [
        '30-60 second cinematic trailer',
        'Film-quality visuals & pacing',
        'Dramatic music & sound design',
        'Professional motion graphics',
        '2 revisions included',
        'Delivered in 7-10 days'
      ],
      highlight: false
    },
    {
      name: 'Trailer + Promo Cuts',
      price: '$850 - $1,000',
      description: 'Full cinematic trailer plus 3 platform-ready promotional cuts',
      features: [
        'Everything in Cinematic Trailer',
        '3 short promo cuts (15-30s each)',
        'Optimized for TikTok, Reels, YouTube',
        'Vertical + horizontal formats',
        'Ready-to-post social assets',
        '3 revisions included'
      ],
      highlight: true
    }
  ];

  return (
    <section id="promo-services" className="py-24 bg-[#0A0A0A] relative overflow-hidden" data-testid="services-section">
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-heading tracking-tight uppercase mb-6" data-testid="services-heading">
            <span className="text-white">Simple </span>
            <span className="text-gradient-noir">Pricing</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto">
            Two options. No confusion. Just cinematic trailers that sell books.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`relative bg-[#0A0A0A] border p-8 flex flex-col transition-all duration-300 ${
                pkg.highlight
                  ? 'border-red-700/60 hover:border-red-600'
                  : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {pkg.highlight && (
                <div className="absolute -top-3 left-8">
                  <span className="bg-red-700 text-white text-[10px] font-bold px-4 py-1 uppercase tracking-[0.2em]" data-testid="best-value-badge">
                    Best Value
                  </span>
                </div>
              )}

              {/* Package Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-heading uppercase tracking-wide text-white mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-gradient-noir mb-3 font-heading tracking-wide">{pkg.price}</div>
                <p className="text-gray-500 text-sm">{pkg.description}</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-gray-300 text-sm">
                    <Check size={16} className="mr-3 mt-0.5 text-red-600 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => scrollToSection('contact')}
                className={`w-full group flex items-center justify-center py-4 font-bold uppercase tracking-[0.15em] text-sm transition-all duration-200 ${
                  pkg.highlight
                    ? 'bg-red-700 hover:bg-red-800 text-white'
                    : 'bg-transparent border border-white/20 hover:border-white/40 hover:bg-white/5 text-white'
                }`}
                data-testid={`pricing-cta-${index}`}
              >
                Request a Trailer Concept
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="border border-white/10 p-8 md:p-12" data-testid="how-it-works">
          <h3 className="text-3xl font-heading uppercase tracking-wide text-white text-center mb-10">How It Works</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Send Your Synopsis', desc: 'Share your book synopsis, cover art, and any visual references you love.' },
              { num: '02', title: 'We Develop the Concept', desc: 'We craft a cinematic vision for your trailer - mood, pacing, and key moments.' },
              { num: '03', title: 'We Deliver Final Assets', desc: 'You receive your finished trailer, ready to launch across all platforms.' }
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className="text-5xl font-heading text-red-700/40 group-hover:text-red-700/70 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h4 className="text-lg font-bold uppercase tracking-wide text-white mb-2">{step.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
